import resolve from './resolve.js';
import merge from './merge.js';

/**
 * mergeObject(object, newObject, valueMap)
 *
 * Returns a new object where keys specified in valueMap are overwritten with values
 * resolved from newObject. Target and source paths support the same notation as
 * resolve()/merge() (dot/slash, array indices, and trailing [] on final segment).
 *
 * Non-mutating: original `object` and `newObject` are not modified.
 *
 * Example:
 *  mergeObject(
 *    { priceListId: null, priceListIdName: null },
 *    { id: 'EUR', idName: 'Euro' },
 *    { priceListId: 'id', priceListIdName: 'idName' }
 *  )
 *  => { priceListId: 'EUR', priceListIdName: 'Euro' }
 *
 * @param {Object} object - Base object to copy and update.
 * @param {Object} newObject - Source object to read values from.
 * @param {Object} valueMap - Mapping of targetPathInObject -> sourcePathInNewObject.
 * @returns {Object} New object with mapped values merged in.
 */
function mergeObject(object, newObject, valueMap) {
  const base = isPlainObject(object) ? object : {};
  // If newObject is an array, take the first element as the source
  const sourceCandidate = Array.isArray(newObject) ? (newObject[0] ?? {}) : newObject;
  const src = isPlainObject(sourceCandidate) ? sourceCandidate : {};
  const map = isPlainObject(valueMap) ? valueMap : {};

  // Start from a shallow copy to keep this non-mutating
  let out = { ...base };

  for (const [targetPath, sourcePath] of Object.entries(map)) {
    const val = resolve(sourcePath, src);
    // Allow overwriting with null/undefined; if you want to skip nullish, add a guard here.
    out = merge(out, val, String(targetPath));
  }

  return out;
}

function isPlainObject(val) {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

export default mergeObject;
